import React, { useState } from 'react';
import { Copy, Check, Code, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { KeywordResearchProject } from '../../types';
import { Button } from '../UI/Button';

interface FaqSchemaWorkstationProps {
  project: KeywordResearchProject;
  masterPrimary: string;
  masterSecondary: string;
}

export const FaqSchemaWorkstation: React.FC<FaqSchemaWorkstationProps> = ({
  project,
  masterPrimary,
  masterSecondary,
}) => {
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  const [expandedFaqIdx, setExpandedFaqIdx] = useState<number | null>(0);

  const businessName = project.businessProfile.businessName || 'Business';
  const location = project.businessProfile.confirmedServices[0] || 'Target Region';

  // Default FAQ items
  const faqItems = [
    {
      question: `Where can I buy certified ${masterPrimary || 'truck parts'} in ${location}?`,
      answer: `You can purchase certified ${masterPrimary || 'truck parts'} directly from ${businessName} in ${location}, offering guaranteed OEM fitment, full warranty coverage, and regional fleet delivery.`
    },
    {
      question: `Do you offer commercial warranty on ${masterSecondary || 'auto parts'}?`,
      answer: `Yes, all ${masterSecondary || 'auto parts'} and commercial spares supplied by ${businessName} come backed by official manufacturer warranties and expert fitment support.`
    }
  ];

  // Generate JSON-LD Schema
  const schemaObj = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const schemaJsonString = JSON.stringify(schemaObj, null, 2);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(`<script type="application/ld+json">\n${schemaJsonString}\n</script>`);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* HEADER BANNER */}
      <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Code size={18} color="var(--accent-blue)" />
          <span>STEP 15: FAQ DIRECT ANSWER SCHEMA & FEATURED SNIPPETS WORKSTATION</span>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Instructor Methodology: Generate valid <strong>JSON-LD FAQ Schema</strong> code from your finalized Q&A pairs to trigger rich snippet accordion dropdowns directly on Google Search Results Pages.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
        
        {/* LEFT PANEL: JSON-LD SCHEMA CODE GENERATOR */}
        <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Code size={16} /> JSON-LD FAQ SCHEMA MARKUP CODE
            </div>
            <Button
              variant="secondary"
              size="sm"
              style={{ fontSize: '11px', background: copiedCode ? '#166534' : 'rgba(56, 189, 248, 0.15)', borderColor: '#38bdf8', color: '#fff' }}
              onClick={handleCopyCode}
            >
              {copiedCode ? <Check size={12} style={{ marginRight: '4px' }} /> : <Copy size={12} style={{ marginRight: '4px' }} />}
              {copiedCode ? 'Copied Code!' : 'Copy Schema Code'}
            </Button>
          </div>

          <textarea
            readOnly
            rows={14}
            className="input-field"
            style={{ fontSize: '11px', fontFamily: 'monospace', color: '#38bdf8', background: '#090d16', width: '100%', lineHeight: '1.4' }}
            value={`<script type="application/ld+json">\n${schemaJsonString}\n</script>`}
          />
        </div>

        {/* RIGHT PANEL: GOOGLE SERP ACCORDION PREVIEW */}
        <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Sparkles size={16} /> GOOGLE SERP RICH ACCORDION PREVIEW
          </div>

          <div style={{ background: '#ffffff', color: '#202124', borderRadius: '8px', padding: '16px', fontFamily: 'arial, sans-serif' }}>
            <div style={{ fontSize: '12px', color: '#202124', marginBottom: '2px' }}>{businessName}</div>
            <div style={{ fontSize: '18px', color: '#1a0dab', fontWeight: 400, marginBottom: '4px', cursor: 'pointer' }}>
              {masterPrimary} | Official Supplier in {location}
            </div>
            <div style={{ fontSize: '13px', color: '#4d5156', lineHeight: '1.4', marginBottom: '12px' }}>
              Shop top quality {masterPrimary} and {masterSecondary} in {location}. Genuine parts with full warranty.
            </div>

            {/* ACCORDION ITEMS */}
            <div style={{ borderTop: '1px solid #dadce0', paddingTop: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#70757a', marginBottom: '6px', textTransform: 'uppercase' }}>
                People also ask:
              </div>
              {faqItems.map((item, idx) => {
                const isOpen = expandedFaqIdx === idx;
                return (
                  <div key={`serp-faq-${idx}`} style={{ borderBottom: '1px solid #dadce0', padding: '8px 0' }}>
                    <div
                      onClick={() => setExpandedFaqIdx(isOpen ? null : idx)}
                      style={{ fontSize: '13px', color: '#1a0dab', fontWeight: 500, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>{item.question}</span>
                      {isOpen ? <ChevronUp size={14} color="#70757a" /> : <ChevronDown size={14} color="#70757a" />}
                    </div>
                    {isOpen && (
                      <div style={{ fontSize: '12px', color: '#4d5156', marginTop: '6px', lineHeight: '1.4', paddingLeft: '4px' }}>
                        {item.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
